import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useLocalStorage from "use-local-storage";
import {
  IconAddressBook,
  IconChevronRight,
  IconPencil,
  IconPlus,
} from "@tabler/icons-react";
import EmptyState from "../UI/EmptyState";
import UIButton from "../UI/Button";
import { Table } from "../UI/Table";
// @ts-expect-error pure js
import useContacts from "../../hooks/use-contacts.js";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useMediaQuery from "../../hooks/use-media-query";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
import { BalanceDomain } from "../../utils/constants/balances";
// @ts-expect-error pure js
import { TenantStatus } from "../../models/tenant_old.js";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
import ContactForm from "./ContactForm";
import Card from "../UI/Card";
import Text from "../UI/Text";
import Status from "../UI/Status";
import Avatar from "../UI/Avatar";
import IconButton from "../UI/IconButton";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection from "../UI/FilterBar/FilterSection";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import { getInitialsFromName } from "../Profile/profileUtils";
import FieldRow from "../Profile/FieldRow";
import { Button } from "@base-ui/react/button";
import type { Contact } from "../../models/carnetAdresse";
import type { Property } from "../../models/property";
import type { Tenant } from "../../models/tenant";
import type { Profile } from "../Profile/types";
import styles from "./ContactShow.module.css";

interface NamedEntity {
  id: number;
  nom: string;
  id_profile_nom_owner?: number;
}

type RowKind = "contact" | "tenant" | "garant";

interface UnifiedRow {
  key: string;
  kind: RowKind;
  nom: string;
  prenom: string;
  mail?: string;
  telephone?: string;
  entreprise?: string;
  affectationLabel: string;
  propertyId?: number;
  tenantId?: number;
  contact?: Contact;
}

interface SelectOption {
  value: number;
  label: string;
}

const TYPE_OPTIONS = [
  { value: "contact", label: "Contact" },
  { value: "tenant", label: "Locataire" },
  { value: "garant", label: "Garant" },
];

const KIND_STATUS: Record<
  Exclude<RowKind, "contact">,
  { color: "success" | "warning"; text: string }
> = {
  tenant: { color: "success", text: "Locataire" },
  garant: { color: "warning", text: "Garant" },
};

function getContactAffectationLabel(
  contact: Contact,
  propertiesById: Record<number, Property>,
  propertyFoldersById: Record<number, NamedEntity>,
  tenantsById: Record<number, NamedEntity>,
): string {
  if (contact.domaine === BalanceDomain.PROPERTY && contact.id_bien) {
    return propertiesById[contact.id_bien]?.nom ?? "";
  }
  if (contact.domaine === BalanceDomain.FOLDER && contact.id_property_folder) {
    return propertyFoldersById[contact.id_property_folder]?.nom ?? "";
  }
  if (contact.domaine === BalanceDomain.TENANT && contact.id_tenant) {
    return tenantsById[contact.id_tenant]?.nom ?? "";
  }
  return "";
}

function getContactPropertyId(
  contact: Contact,
  tenantsById: Record<number, Tenant>,
): number | undefined {
  if (contact.domaine === BalanceDomain.PROPERTY) {
    return contact.id_bien;
  }
  if (contact.domaine === BalanceDomain.TENANT && contact.id_tenant) {
    return tenantsById[contact.id_tenant]?.id_bien;
  }
  return undefined;
}

function contactProfileId(
  contact: Contact,
  propertiesById: Record<number, Property>,
  propertyFoldersById: Record<number, NamedEntity>,
  tenantsById: Record<number, Tenant>,
): number | null {
  if (
    contact.domaine === BalanceDomain.PROFILE_BAILLEUR &&
    contact.id_profile_bailleur
  ) {
    return contact.id_profile_bailleur;
  }
  if (contact.domaine === BalanceDomain.PROPERTY && contact.id_bien) {
    return propertiesById[contact.id_bien]?.id_profile_nom_owner ?? null;
  }
  if (contact.domaine === BalanceDomain.FOLDER && contact.id_property_folder) {
    return (
      propertyFoldersById[contact.id_property_folder]?.id_profile_nom_owner ??
      null
    );
  }
  if (contact.domaine === BalanceDomain.TENANT && contact.id_tenant) {
    const idBien = tenantsById[contact.id_tenant]?.id_bien;
    if (idBien == null) return null;
    return propertiesById[idBien]?.id_profile_nom_owner ?? null;
  }
  return null;
}

function ContactIdentity({ row }: { row: UnifiedRow }) {
  const fullName = `${row.prenom} ${row.nom}`.trim();
  return (
    <div className={styles.identity}>
      <Avatar initials={getInitialsFromName(fullName)} />
      <div>
        <Text as="div" weight="bold">
          {fullName}
        </Text>
        {row.mail && (
          <Text as="div" variant="deci" color="muted">
            {row.mail}
          </Text>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  row: UnifiedRow;
  profileId: number | null;
  onAction: () => void;
}

function ContactRowDesktop({ row, profileId, onAction }: RowProps) {
  const kindStatus = row.kind === "contact" ? null : KIND_STATUS[row.kind];
  const isContact = row.kind === "contact";

  return (
    <Table.Row onClick={onAction} className={styles.desktopRow}>
      <Table.Cell>
        <Text as="div" weight="bold">
          {`${row.prenom} ${row.nom}`.trim()}
        </Text>
        {kindStatus && (
          <Text as="div" variant="deci" color="muted">
            {kindStatus.text}
          </Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <Text as="div">{row.mail}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text as="div">{row.telephone}</Text>
      </Table.Cell>
      <Table.Cell>
        {row.entreprise && <Status color="info" text={row.entreprise} />}
      </Table.Cell>
      <Table.Cell>
        <Text as="div">{row.affectationLabel}</Text>
      </Table.Cell>
      <Table.Cell align="right">
        <IconButton
          icon={isContact ? IconPencil : IconChevronRight}
          label={isContact ? "Modifier" : "Voir la fiche"}
          variant="plain"
          requiresWrite={isContact}
          profileId={isContact ? profileId : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onAction();
          }}
        />
      </Table.Cell>
    </Table.Row>
  );
}

function ContactRowMobile({ row, onAction }: Omit<RowProps, "profileId">) {
  const kindStatus = row.kind === "contact" ? null : KIND_STATUS[row.kind];
  return (
    <Card
      padding="sm"
      as={Button}
      onClick={onAction}
      className={styles.mobileCard}
    >
      <div className={styles.mobileHeader}>
        <ContactIdentity row={row} />
        {kindStatus ? (
          <Status color={kindStatus.color} text={kindStatus.text} />
        ) : (
          row.entreprise && <Status color="info" text={row.entreprise} />
        )}
      </div>
      <FieldRow label="Téléphone" value={row.telephone} />
      <FieldRow label="Affectation" value={row.affectationLabel} />
    </Card>
  );
}

export default function ContactShow() {
  const isSmallScreen = useMediaQuery("(max-width: 799px)");
  const navigate = useNavigate();
  const { contacts, isLoading, deleteContact, updateContact, createContact } =
    useContacts() as {
      contacts: Contact[];
      isLoading: boolean;
      deleteContact: (id: number) => Promise<void>;
      updateContact: (
        id: number,
        values: Record<string, unknown>,
      ) => Promise<void>;
      createContact: (values: Record<string, unknown>) => Promise<void>;
    };
  const { properties, propertiesById } = useProperties({ withById: true }) as {
    properties: Property[];
    propertiesById: Record<number, Property>;
  };
  const { propertyFoldersById } = usePropertyFolders({ withById: true }) as {
    propertyFoldersById: Record<number, NamedEntity>;
  };
  const { tenants, tenantsById } = useTenants({ withById: true }) as {
    tenants: Tenant[];
    tenantsById: Record<number, Tenant>;
  };
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const [search, setSearch] = useState("");
  const [filterTypes, setFilterTypes] = useLocalStorage<string[]>(
    "carnetAdresse_typeFilter",
    [],
  );
  const [filterPropertyIds, setFilterPropertyIds] = useLocalStorage<number[]>(
    "carnetAdresse_propertyFilter",
    [],
  );
  const [filterTenantIds, setFilterTenantIds] = useLocalStorage<number[]>(
    "carnetAdresse_tenantFilter",
    [],
  );

  async function submitContactHandler(values: Record<string, unknown>) {
    const sanitizedValues = { ...sanitizeValues(values) };

    if (contactToEdit) {
      await updateContact(contactToEdit.id, sanitizedValues);
    } else {
      await createContact(sanitizedValues);
    }
    setContactToEdit(null);
    setIsContactModalOpen(false);
  }

  function handleAddClick() {
    setContactToEdit(null);
    setIsContactModalOpen(true);
  }

  function handleContactClick(contact: Contact) {
    setContactToEdit(contact);
    setIsContactModalOpen(true);
  }

  const rows = useMemo<UnifiedRow[]>(() => {
    const contactRows: UnifiedRow[] = contacts.map((contact) => ({
      key: `contact-${contact.id}`,
      kind: "contact",
      contact,
      nom: contact.nom,
      prenom: contact.prenom ?? "",
      mail: contact.mail,
      telephone: contact.telephone,
      entreprise: contact.entreprise,
      affectationLabel: getContactAffectationLabel(
        contact,
        propertiesById,
        propertyFoldersById,
        tenantsById,
      ),
      propertyId: getContactPropertyId(contact, tenantsById),
      tenantId:
        contact.domaine === BalanceDomain.TENANT
          ? contact.id_tenant
          : undefined,
    }));

    const isDisplayableProfile = (profile: Profile) =>
      profile.id !== undefined &&
      profile.status === ProfileStatus.Actif.status &&
      (profile.nom || profile.prenom || profile.nom_societe);

    const buildProfileRow = (
      tenant: Tenant,
      profile: Profile,
      kind: Exclude<RowKind, "contact">,
      propertyName: string,
    ): UnifiedRow => {
      const isSociete = parseInt(String(profile.type)) === 2;
      return {
        key: `tenant-${tenant.id}-${kind}-${profile.id}`,
        kind,
        tenantId: tenant.id,
        propertyId: tenant.id_bien,
        nom: (isSociete ? profile.nom_societe : profile.nom) ?? "",
        prenom: isSociete ? "" : profile.prenom ?? "",
        mail: profile.mail,
        telephone: profile.tel,
        affectationLabel: propertyName,
      };
    };

    const profileRows: UnifiedRow[] = tenants
      .filter((tenant) => tenant.status === TenantStatus.Actif.status)
      .flatMap((tenant) => {
        const propertyName = propertiesById[tenant.id_bien]?.nom ?? tenant.nom;
        return (tenant.profiles ?? [])
          .filter(isDisplayableProfile)
          .flatMap((profile): UnifiedRow[] => [
            buildProfileRow(tenant, profile, "tenant", propertyName),
            ...(profile.garantors ?? [])
              .filter(isDisplayableProfile)
              .map((garant) =>
                buildProfileRow(tenant, garant, "garant", propertyName),
              ),
          ]);
      });

    const allContacts = [...contactRows, ...profileRows];
    allContacts.sort((a, b) =>
      a.nom.toLowerCase() > b.nom.toLowerCase() ? 1 : -1,
    );
    return allContacts;
  }, [contacts, tenants, propertiesById, propertyFoldersById, tenantsById]);

  const filteredRows = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    const typeSet = new Set(filterTypes);
    const propertySet = new Set(filterPropertyIds);
    const tenantSet = new Set(filterTenantIds);

    return rows.filter((row) => {
      if (typeSet.size > 0 && !typeSet.has(row.kind)) return false;
      if (
        propertySet.size > 0 &&
        (row.propertyId === undefined || !propertySet.has(row.propertyId))
      ) {
        return false;
      }
      if (
        tenantSet.size > 0 &&
        (row.tenantId === undefined || !tenantSet.has(row.tenantId))
      ) {
        return false;
      }
      if (!searchLower) return true;
      return (
        row.nom.toLowerCase().includes(searchLower) ||
        row.prenom.toLowerCase().includes(searchLower) ||
        row.mail?.toLowerCase().includes(searchLower) ||
        row.telephone?.toLowerCase().includes(searchLower) ||
        row.entreprise?.toLowerCase().includes(searchLower) ||
        row.affectationLabel.toLowerCase().includes(searchLower)
      );
    });
  }, [rows, search, filterTypes, filterPropertyIds, filterTenantIds]);

  const propertyOptions: SelectOption[] = useMemo(
    () =>
      properties
        .map((p) => ({ value: p.id, label: p.nom }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [properties],
  );

  const tenantOptions: SelectOption[] = useMemo(
    () =>
      tenants
        .filter((t) => t.status === TenantStatus.Actif.status)
        .map((t) => ({ value: t.id, label: t.nom }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [tenants],
  );

  const selectedPropertyOptions = filterPropertyIds
    .map((id) => propertyOptions.find((o) => o.value === id))
    .filter((o): o is SelectOption => Boolean(o));

  const selectedTenantOptions = filterTenantIds
    .map((id) => tenantOptions.find((o) => o.value === id))
    .filter((o): o is SelectOption => Boolean(o));

  const activeFiltersCount =
    (filterTypes.length > 0 ? 1 : 0) +
    (filterPropertyIds.length > 0 ? 1 : 0) +
    (filterTenantIds.length > 0 ? 1 : 0);

  function clearFilters() {
    setFilterTypes([]);
    setFilterPropertyIds([]);
    setFilterTenantIds([]);
  }

  function handleRowAction(row: UnifiedRow) {
    if (row.kind === "contact" && row.contact) {
      handleContactClick(row.contact);
    } else if (row.tenantId !== undefined) {
      navigate(`/bailleur/tenants/${row.tenantId}`);
    }
  }

  if (isLoading) return <IsLoading />;

  const contactToEditProfileId = contactToEdit
    ? contactProfileId(
        contactToEdit,
        propertiesById,
        propertyFoldersById,
        tenantsById,
      )
    : null;

  const modal = (
    <ContactForm
      open={isContactModalOpen}
      onOpenChange={(open) => {
        setIsContactModalOpen(open);
        if (!open) setContactToEdit(null);
      }}
      contact={contactToEdit}
      profileId={contactToEditProfileId}
      onSubmit={submitContactHandler}
      onDelete={async (id: number) => {
        await deleteContact(id);
        setIsContactModalOpen(false);
      }}
    />
  );

  const filterBar = (
    <FilterBar
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Rechercher un contact"
      activeFiltersCount={activeFiltersCount}
      onClearFilters={clearFilters}
      addLabel="Ajouter un contact"
      onAdd={handleAddClick}
    >
      <FilterSection
        title="Type"
        options={TYPE_OPTIONS}
        values={filterTypes}
        onChange={setFilterTypes}
        defaultOpen
      />
      <ReactSelectStandalone
        label="Biens"
        isMulti
        options={propertyOptions}
        value={selectedPropertyOptions}
        onChange={(opts: SelectOption[] | null) =>
          setFilterPropertyIds((opts || []).map((o) => o.value))
        }
        placeholder="Tous les biens"
        menuPortalTarget={document.body}
      />
      <ReactSelectStandalone
        label="Locataires"
        isMulti
        options={tenantOptions}
        value={selectedTenantOptions}
        onChange={(opts: SelectOption[] | null) =>
          setFilterTenantIds((opts || []).map((o) => o.value))
        }
        placeholder="Tous les locataires"
        menuPortalTarget={document.body}
      />
    </FilterBar>
  );

  if (rows.length === 0) {
    return (
      <>
        {modal}
        <EmptyState
          icon={IconAddressBook}
          title="Aucun contact pour le moment"
          description="Ajoutez vos artisans, syndics ou assureurs pour retrouver leurs coordonnées au même endroit, aux côtés de celles de vos locataires."
        >
          <UIButton icon={IconPlus} onClick={handleAddClick}>
            Ajouter un contact
          </UIButton>
        </EmptyState>
      </>
    );
  }

  if (filteredRows.length === 0) {
    return (
      <>
        {modal}
        {filterBar}
        <Card padding="lg" className={styles.emptyState}>
          Aucun contact trouvé
        </Card>
      </>
    );
  }

  if (isSmallScreen) {
    return (
      <>
        {modal}
        {filterBar}
        <div className={styles.mobileContainer}>
          {filteredRows.map((row) => (
            <ContactRowMobile
              key={row.key}
              row={row}
              onAction={() => handleRowAction(row)}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {modal}
      {filterBar}
      <Table variant="card">
        <Table.Header>
          <Table.Column>Prénom &amp; Nom</Table.Column>
          <Table.Column>E-mail</Table.Column>
          <Table.Column>Téléphone</Table.Column>
          <Table.Column>Société</Table.Column>
          <Table.Column>Affectation</Table.Column>
          <Table.Column align="right"></Table.Column>
        </Table.Header>
        <Table.Body>
          {filteredRows.map((row) => (
            <ContactRowDesktop
              key={row.key}
              row={row}
              profileId={
                row.kind === "contact" && row.contact
                  ? contactProfileId(
                      row.contact,
                      propertiesById,
                      propertyFoldersById,
                      tenantsById,
                    )
                  : null
              }
              onAction={() => handleRowAction(row)}
            />
          ))}
        </Table.Body>
      </Table>
    </>
  );
}
